import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical,
  Building2, Type, Mail, Phone, FileText, List, ToggleLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto corto', icon: Type },
  { value: 'textarea', label: 'Texto largo', icon: FileText },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Teléfono', icon: Phone },
  { value: 'select', label: 'Selección', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: ToggleLeft },
];

const SortableField = ({ field, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
    >
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="mt-2 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={20} />
        </button>
        
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Etiqueta</Label>
              <Input
                value={field.label}
                onChange={(e) => onUpdate(field.id, 'label', e.target.value)}
                className="bg-zinc-800 border-zinc-700 mt-1"
                placeholder="Nombre del campo"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Tipo</Label>
              <select
                value={field.type}
                onChange={(e) => onUpdate(field.id, 'type', e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Placeholder</Label>
              <Input
                value={field.placeholder || ''}
                onChange={(e) => onUpdate(field.id, 'placeholder', e.target.value)}
                className="bg-zinc-800 border-zinc-700 mt-1"
                placeholder="Texto de ayuda"
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => onUpdate(field.id, 'required', e.target.checked)}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-[#D4AF37]"
                />
                <span className="text-sm text-zinc-400">Obligatorio</span>
              </label>
            </div>
          </div>
          
          {field.type === 'select' && (
            <div>
              <Label className="text-xs text-zinc-500">Opciones (una por línea)</Label>
              <Textarea
                value={field.options?.join('\n') || ''}
                onChange={(e) => onUpdate(field.id, 'options', e.target.value.split('\n').filter(o => o.trim()))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                rows={3}
                placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
              />
            </div>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(field.id)}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <Trash2 size={18} />
        </Button>
      </div>
    </div>
  );
};

const PartnerFormEditorPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formConfig, setFormConfig] = useState({
    title: 'Quiero ser Partner',
    description: 'Completa el formulario para aplicar como partner de Bravo',
    fields: []
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadFormConfig();
  }, []);

  const loadFormConfig = async () => {
    try {
      const response = await api.get('/settings/form-partner');
      if (response.data) {
        setFormConfig(response.data);
      }
    } catch (error) {
      // If not found, use defaults
      setFormConfig({
        title: 'Quiero ser Partner',
        description: 'Completa el formulario para unirte a nuestra red de partners y ofrecer beneficios exclusivos a la comunidad BRAVX.',
        fields: [
          { id: 'field_1', label: 'Nombre del negocio', type: 'text', required: true, placeholder: 'Ej: Bar La Noche' },
          { id: 'field_2', label: 'Tipo de negocio', type: 'select', required: true, options: ['Bar', 'Restaurante', 'Club', 'Tienda', 'Otro'] },
          { id: 'field_3', label: 'Dirección', type: 'text', required: true, placeholder: 'Dirección completa' },
          { id: 'field_4', label: 'Nombre de contacto', type: 'text', required: true, placeholder: 'Tu nombre' },
          { id: 'field_5', label: 'Email', type: 'email', required: true, placeholder: 'email@ejemplo.com' },
          { id: 'field_6', label: 'Teléfono', type: 'phone', required: true, placeholder: '+54 11 1234-5678' },
          { id: 'field_7', label: 'Descripción del beneficio propuesto', type: 'textarea', required: true, placeholder: 'Describe qué beneficio ofrecerías a los miembros BRAVX' },
          { id: 'field_8', label: 'Redes sociales / Web', type: 'text', required: false, placeholder: '@instagram o sitio web' },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/form-partner', formConfig);
      toast.success('Formulario guardado');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'Nuevo campo',
      type: 'text',
      required: false,
      placeholder: ''
    };
    setFormConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (id, key, value) => {
    setFormConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === id ? { ...f, [key]: value } : f)
    }));
  };

  const deleteField = (id) => {
    setFormConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== id)
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFormConfig(prev => {
        const oldIndex = prev.fields.findIndex(f => f.id === active.id);
        const newIndex = prev.fields.findIndex(f => f.id === over.id);
        return {
          ...prev,
          fields: arrayMove(prev.fields, oldIndex, newIndex)
        };
      });
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center min-h-screen">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="partner-form-editor">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link 
                to="/admin/formularios" 
                className="inline-flex items-center text-zinc-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft size={16} className="mr-2" />
                Volver a Formularios
              </Link>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Building2 className="text-[#D4AF37]" />
                Formulario de Partner
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                Configura los campos del formulario de solicitud
              </p>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Guardar
            </Button>
          </div>

          {/* Form Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Basic Info */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h2 className="font-bold text-white">Información del Formulario</h2>
              <div>
                <Label className="text-zinc-400">Título</Label>
                <Input
                  value={formConfig.title}
                  onChange={(e) => setFormConfig(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Descripción</Label>
                <Textarea
                  value={formConfig.description}
                  onChange={(e) => setFormConfig(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  rows={3}
                />
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-white">Campos del Formulario</h2>
                <Button
                  onClick={addField}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <Plus size={16} className="mr-2" />
                  Añadir Campo
                </Button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={formConfig.fields.map(f => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {formConfig.fields.map((field) => (
                      <SortableField
                        key={field.id}
                        field={field}
                        onUpdate={updateField}
                        onDelete={deleteField}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {formConfig.fields.length === 0 && (
                <div className="text-center py-12 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <FileText size={48} className="mx-auto mb-4 text-zinc-700" />
                  <p>No hay campos configurados</p>
                  <p className="text-sm">Añade campos para construir el formulario</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default PartnerFormEditorPage;
